/**
 * AI Card Identifier
 *
 * Uses Claude Vision API as a fallback when OCR confidence is low.
 * Makes requests through a Firebase Cloud Function to avoid exposing API keys.
 */

export interface AIIdentificationResult {
  cardName: string;
  setName: string;
  setNumber?: string;
  confidence: number;
  variant?: 'normal' | 'holofoil' | 'reverse_holofoil' | 'full_art' | 'special_art';
  condition?: string;
  additionalInfo?: string;
}

const AI_PROXY_URL = process.env.REACT_APP_AI_PROXY_URL || '/api/ai';

/**
 * Identify card using Claude Vision
 */
export async function identifyCardWithAI(
  imageBase64: string
): Promise<AIIdentificationResult> {
  try {
    const response = await fetch(`${AI_PROXY_URL}/identify-card`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        image: imageBase64,
      }),
    });

    if (!response.ok) {
      throw new Error(`AI identification failed: ${response.status}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('AI identification error:', error);
    throw error;
  }
}

/**
 * Mock AI identification for development/testing
 * Returns a simulated response when the AI proxy is not available
 */
export async function mockIdentifyCard(
  imageBase64: string
): Promise<AIIdentificationResult> {
  // Simulate network delay
  await new Promise(resolve => setTimeout(resolve, 1500));

  // Return mock result
  return {
    cardName: 'Unknown Card',
    setName: 'Unknown Set',
    confidence: 50,
    additionalInfo: 'AI identification not configured. Please set up the AI proxy.',
  };
}

/**
 * Check if AI identification is available
 */
export function isAIAvailable(): boolean {
  return !!process.env.REACT_APP_AI_PROXY_URL;
}

/**
 * Smart identification that tries AI only when needed
 */
export async function smartIdentify(
  imageBase64: string,
  ocrConfidence: number,
  ocrThreshold: number = 70
): Promise<{
  usedAI: boolean;
  result: AIIdentificationResult | null;
}> {
  // If OCR confidence is high enough, skip AI
  if (ocrConfidence >= ocrThreshold) {
    return { usedAI: false, result: null };
  }

  // Try AI identification
  if (!isAIAvailable()) {
    console.log('AI proxy not configured, using mock');
    const mockResult = await mockIdentifyCard(imageBase64);
    return { usedAI: true, result: mockResult };
  }

  try {
    const result = await identifyCardWithAI(imageBase64);
    return { usedAI: true, result };
  } catch (error) {
    console.error('AI identification failed:', error);
    return { usedAI: true, result: null };
  }
}

/**
 * Build the prompt for Claude Vision
 * This would be used by the Firebase function
 */
export function buildIdentificationPrompt(): string {
  return `You are analyzing an image of a Pokemon Trading Card Game card.

Please identify the following information from the card:

1. **Card Name**: The name of the Pokemon or card (e.g., "Charizard ex", "Pikachu V")
2. **Set Name**: The expansion set the card belongs to (e.g., "Pokemon 151", "Scarlet & Violet")
3. **Set Number**: The card number in the set, usually shown as "XXX/YYY" at the bottom
4. **Card Variant**: Is this a normal, holofoil, reverse holofoil, full art, or special art card?
5. **Condition**: If visible, estimate the condition (Mint, Near Mint, Lightly Played, etc.)

Respond in JSON format:
{
  "cardName": "string",
  "setName": "string",
  "setNumber": "string or null",
  "variant": "normal|holofoil|reverse_holofoil|full_art|special_art",
  "condition": "string or null",
  "confidence": number (0-100),
  "additionalInfo": "any additional observations"
}

Only respond with the JSON, no other text.`;
}
